import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Col } from 'src/components/Col';
import Layout from 'src/components/layout';
import { Row } from 'src/components/Row';
import { Table } from 'src/components/Table';
import { RangeDatePicker } from 'src/components/datepicker';
import { useDispatch, useSelector } from 'react-redux';
import { getTimestampOf } from 'src/utils/helpers';
import * as ActionTypes from 'src/store/actions';
import { BusiestDayRow, ResultRow, Form, PointProps, Polygon } from './types';
import DrawAnnotations from './component/draw';
import CoordinateInputs from './component/CoordinateInputs';
import { fetchPolygonMetrics, savePolygon, fetchPolygons, deletePolygon } from './services/api';
import { normalizedToPixel, pixelToNormalized, generateId, generateDefaultPolygon } from './utils/coordinateUtils';
import { notifyError, notifyInfo, notifySuccess } from 'src/utils/notification';
import { AiOutlinePlus, AiOutlineSave, AiOutlineDelete, AiOutlineReload, AiOutlineSearch } from 'react-icons/ai';
import { STORES } from './component/data';
import { MdSafetyDivider } from 'react-icons/md';
import { BiArrowToBottom, BiDownArrow } from 'react-icons/bi';
import { BsArrowDown } from 'react-icons/bs';
import { IoArrowDown, IoArrowDownOutline } from 'react-icons/io5';

const CANVAS_WIDTH = 550;
const CANVAS_HEIGHT = 350;

const Sandbox: React.FC = () => {
  const dispatch = useDispatch();
  const { startDate, endDate } = useSelector((state: any) => state.dateTime);
  const startTimeTimestamp = Math.trunc(getTimestampOf(new Date(startDate)));
  const endTimeTimestamp = Math.trunc(getTimestampOf(new Date(endDate)));
  const { cameraImage, cameraImageLoading } = useSelector((state: any) => state.cameras);

  const [storeId, setStoreId] = useState(STORES[1].id);
  const [deptId, setDeptId] = useState(STORES[1].deparmentId);
  const [camId, setCamId] = useState(STORES[1].cameras[0].id);

  const [polygons, setPolygons] = useState<Polygon[]>([]);
  const [activePolygonIndex, setActivePolygonIndex] = useState(-1);
  const [newPolygonName, setNewPolygonName] = useState('');

  const [results, setResults] = useState<ResultRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busiestDays, setBusiestDays] = useState<BusiestDayRow[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [deletedBackendPolygons, setDeletedBackendPolygons] = useState<number[]>([]);

  const notify = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    switch (type) {
      case 'success':
        notifySuccess(message);
        break;
      case 'error':
        notifyError(message);
        break;
      case 'info':
        notifyInfo(message);
        break;
      default:
        notifySuccess(message);
    }
  };

  useEffect(() => {
    dispatch(ActionTypes.getCameraImage.request({ cameraId: camId }));
  }, [dispatch, camId]);

  useEffect(() => {
    const loadInitialPolygons = async () => {
      try {
        setIsLoading(true);
        const loadedPolygons = await fetchPolygons(CANVAS_WIDTH, CANVAS_HEIGHT, camId);

        if (loadedPolygons.length > 0) {
          const polygonsWithSelection = loadedPolygons.map((polygon) => ({
            ...polygon,
            isSelected: false,
          }));

          setPolygons(polygonsWithSelection);
          setActivePolygonIndex(-1);
        } else {
          setPolygons([]);
          setActivePolygonIndex(-1);
        }
      } catch (error) {
        console.error('Error loading initial polygons:', error);
        notify('Failed to load initial polygons from the backend', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialPolygons();
  }, [camId]);

  useEffect(() => {
    if (polygons.length > 0 || deletedBackendPolygons.length > 0) {
      setHasChanges(true);
    }
  }, [polygons, deletedBackendPolygons]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<Form>({});

  useEffect(() => {
    if (polygons.length === 0 || activePolygonIndex < 0 || activePolygonIndex >= polygons.length) {
      setValue('x1', '0.0000');
      setValue('y1', '0.0000');
      setValue('x2', '0.0000');
      setValue('y2', '0.0000');
      setValue('x3', '0.0000');
      setValue('y3', '0.0000');
      setValue('x4', '0.0000');
      setValue('y4', '0.0000');
      return;
    }

    const activePolygon = polygons[activePolygonIndex];
    if (!activePolygon || !activePolygon.points || activePolygon.points.length < 4) return;

    setValue('x1', pixelToNormalized(activePolygon.points[0].x, CANVAS_WIDTH));
    setValue('y1', pixelToNormalized(activePolygon.points[0].y, CANVAS_HEIGHT));
    setValue('x2', pixelToNormalized(activePolygon.points[1].x, CANVAS_WIDTH));
    setValue('y2', pixelToNormalized(activePolygon.points[1].y, CANVAS_HEIGHT));
    setValue('x3', pixelToNormalized(activePolygon.points[2].x, CANVAS_WIDTH));
    setValue('y3', pixelToNormalized(activePolygon.points[2].y, CANVAS_HEIGHT));
    setValue('x4', pixelToNormalized(activePolygon.points[3].x, CANVAS_WIDTH));
    setValue('y4', pixelToNormalized(activePolygon.points[3].y, CANVAS_HEIGHT));
  }, [activePolygonIndex, polygons, setValue]);

  const fetchData = async (formData?: Form) => {
    setIsLoading(true);
    try {
      const formValues = formData || getValues();
      console.log('Sending coordinates:', formValues);

      const response = await fetchPolygonMetrics(formValues, startTimeTimestamp, endTimeTimestamp, deptId, camId, storeId);
      setResults(response.results);
      setBusiestDays(response.busiestDays);
    } catch (error) {
      console.error('Error fetching data:', error);
      setResults([{ metric: 'Error', value: 'Failed to fetch data' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddPolygon = () => {
    const newPolygon = generateDefaultPolygon(newPolygonName);

    const updatedPolygons = polygons.map((polygon) => ({
      ...polygon,
      isSelected: false,
    }));

    setPolygons([...updatedPolygons, newPolygon]);
    setActivePolygonIndex(updatedPolygons.length);
    setNewPolygonName('');
    setHasChanges(true);
  };

  const handleSelectPolygon = (index: number) => {
    if (index < 0) {
      setPolygons(polygons.map((polygon) => ({
        ...polygon,
        isSelected: false,
      })));
      setActivePolygonIndex(-1);
      return;
    }

    if (index >= polygons.length) return;

    const updatedPolygons = polygons.map((polygon, idx) => ({
      ...polygon,
      isSelected: idx === index,
    }));

    setPolygons(updatedPolygons);
    setActivePolygonIndex(index);
  };

  const handleDeletePolygon = (index: number) => {
    if (index < 0 || index >= polygons.length) return;

    const polygonToDelete = polygons[index];
    if (polygonToDelete.backendId !== undefined) {
      setDeletedBackendPolygons((prevIds) => [...prevIds, Number(polygonToDelete.backendId)]);
    }

    const updatedPolygons = [...polygons];
    updatedPolygons.splice(index, 1);

    setPolygons(updatedPolygons);
    setHasChanges(true);

    if (activePolygonIndex === index) {
      const newIndex = updatedPolygons.length > 0 ? Math.min(index, updatedPolygons.length - 1) : -1;

      if (newIndex >= 0) {
        updatedPolygons[newIndex] = {
          ...updatedPolygons[newIndex],
          isSelected: true,
        };
      }

      setActivePolygonIndex(newIndex);
    } else if (activePolygonIndex > index) {
      setActivePolygonIndex(activePolygonIndex - 1);
    }
  };

  const handlePolygonChange = (updatedPolygons: Polygon[]) => {
    setPolygons(updatedPolygons);
    setHasChanges(true);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, coord: 'x' | 'y') => {
    const { value } = e.target;
    const dimension = coord === 'x' ? CANVAS_WIDTH : CANVAS_HEIGHT;
    const pixelValue = normalizedToPixel(value, dimension);

    setPolygons((prev) => {
      if (activePolygonIndex < 0 || activePolygonIndex >= prev.length) return prev;

      const updatedPolygons = [...prev];
      const activePolygon = { ...updatedPolygons[activePolygonIndex] };
      const updatedPoints = [...activePolygon.points];

      if (coord === 'x') {
        updatedPoints[index] = { ...updatedPoints[index], x: pixelValue };
      } else {
        updatedPoints[index] = { ...updatedPoints[index], y: pixelValue };
      }

      activePolygon.points = updatedPoints;
      updatedPolygons[activePolygonIndex] = activePolygon;

      setHasChanges(true);
      return updatedPolygons;
    });
  };

  const handleSaveAllPolygons = async () => {
    try {
      setIsLoading(true);
      const updatedPolygons = [...polygons];
      let savedCount = 0;
      let deletedCount = 0;

      if (deletedBackendPolygons.length > 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (const backendId of deletedBackendPolygons) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await deletePolygon(backendId);
            // eslint-disable-next-line no-plusplus
            deletedCount++;
          } catch (error) {
            console.error(`Error deleting polygon with ID ${backendId}:`, error);
          }
        }
        setDeletedBackendPolygons([]);
      }

      for (let i = 0; i < updatedPolygons.length; i += 1) {
        const polygon = updatedPolygons[i];
        // eslint-disable-next-line no-await-in-loop
        const response = await savePolygon(
          polygon,
          polygon.name || `Polygon ${i + 1}`,
          CANVAS_WIDTH,
          CANVAS_HEIGHT,
          deptId,
          camId,
        );

        if (response && response.id) {
          updatedPolygons[i] = {
            ...updatedPolygons[i],
            backendId: response.id,
          };
          // eslint-disable-next-line no-plusplus
          savedCount++;
        }
      }

      setPolygons(updatedPolygons);

      if (savedCount > 0 && deletedCount > 0) {
        notify(`${savedCount} areas saved and ${deletedCount} areas deleted successfully!`, 'success');
      } else if (savedCount > 0) {
        notify(`${savedCount} areas saved successfully!`, 'success');
      } else if (deletedCount > 0) {
        notify(`${deletedCount} areas deleted successfully!`, 'success');
      } else {
        notify('No changes to save', 'info');
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Error saving/deleting polygons:', error);
      notify('Failed to save changes to the backend', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePolygonNameChange = (index: number, newName: string) => {
    setPolygons((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        name: newName,
      };
      return updated;
    });
    setHasChanges(true);
  };

  const onSubmit = (data: Form) => {
    console.log('Form submitted with data:', data);
    fetchData(data);
  };

  const handleFetchData = () => {
    const formData = getValues();
    console.log('Fetching data with values:', formData);
    fetchData(formData);
  };

  const isFormDisabled = activePolygonIndex < 0 || activePolygonIndex >= polygons.length;

  return (
    <Layout title="" loading={cameraImageLoading}>
      <Row>
        <Col className="flex flex-row pb-2 relative z-50">
          <div className="relative">
            <RangeDatePicker needTime />
          </div>
        </Col>
      </Row>
      <Row className="w-[600px] pb-4">
        <Col className="flex relative z-50">
          <div className="relative w-full">
            <select
              value={storeId}
              onChange={(e) => {
                setPolygons([]);
                setActivePolygonIndex(-1);
                setBusiestDays([]);
                setStoreId(Number(e.target.value));
                setDeptId(STORES.find((store) => store.id === Number(e.target.value))?.deparmentId || 9);
                setCamId(STORES.find((store) => store.id === Number(e.target.value))?.cameras[0].id || 25);
              }}
              className="w-full bg-white border border-gray-300 hover:border-gray-400 px-4 py-2 pr-8 rounded-lg"
            >
              {STORES.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        </Col>
        <Col className="flex relative z-50">
          <div className="relative w-full">
            <select
              value={camId}
              onChange={(e) => {
                setPolygons([]);
                setActivePolygonIndex(-1);
                setBusiestDays([]);
                setCamId(Number(e.target.value));
              }}
              className="w-full bg-white border border-gray-300 hover:border-gray-400 px-4 py-2 pr-8 rounded-lg"
            >
              {STORES.find((store) => store.id === storeId)?.cameras.map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {`${camera.es_cam_id} (id: ${camera.id})`}
                </option>
              ))}
            </select>
          </div>
        </Col>
      </Row>
      <div className="flex flex-col gap-2 ml-2 relative z-0">
        <div className="flex gap-4">
          <Card title="Areas" className="w-72 overflow-auto p-2 flex flex-col">
            <div className="flex items-center mb-4">
              <input
                type="text"
                value={newPolygonName}
                onChange={(e) => setNewPolygonName(e.target.value)}
                placeholder="New area name"
                className="border border-gray-300 rounded px-2 py-1 flex-1 mr-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Button
                onClick={handleAddPolygon}
                className="p-2 bg-blue-600 hover:bg-blue-700 transition-colors"
                title="Add new area"
              >
                <AiOutlinePlus size={18} />
              </Button>
            </div>

            <div className="space-y-2 flex-grow overflow-auto mb-4">
              {polygons.map((polygon, index) => (
                <div
                  key={polygon.id}
                  className={`flex justify-between items-center p-3 rounded-lg ${index === activePolygonIndex
                    ? 'bg-blue-100 border-l-4 border-blue-500 shadow-md'
                    : 'bg-white border border-gray-200 hover:bg-gray-50 transition-colors'
                  }`}
                >
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => handleSelectPolygon(index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSelectPolygon(index);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {index === activePolygonIndex ? (
                      <input
                        type="text"
                        value={polygon.name || `Area ${index + 1}`}
                        onChange={(e) => handlePolygonNameChange(index, e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span className="font-medium">{polygon.name || `Area ${index + 1}`}</span>
                    )}
                  </div>
                  {index === activePolygonIndex && (
                    <div className="flex space-x-1 ml-2">
                      <Button
                        onClick={() => handleDeletePolygon(index)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors"
                        title={polygon.backendId ? 'Delete area' : 'Save area first to enable deletion'}
                      >
                        <AiOutlineDelete size={16} />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {polygons.length === 0 && (
                <div className="text-center text-gray-500 p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex flex-col items-center space-y-2">
                    <p>No areas defined yet. Click the + button to add an area.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-auto flex justify-center">
              <Button
                onClick={handleSaveAllPolygons}
                className="w-200 px-4 py-2 flex items-center justify-center gap-2"
                disabled={!hasChanges || isLoading}
              >
                <AiOutlineSave size={18} />
                Save All Polygons
              </Button>
            </div>
          </Card>

          <Card title="Area Drawing" className="p-2">
            <DrawAnnotations
              polygons={polygons}
              activePolygonIndex={activePolygonIndex}
              onPolygonChange={handlePolygonChange}
              onAddPolygon={handleAddPolygon}
              onSelectPolygon={handleSelectPolygon}
              canvasWidth={CANVAS_WIDTH}
              canvasHeight={CANVAS_HEIGHT}
              imageBackground={cameraImage || null}
            />
            <Button
              onClick={handleFetchData}
              className="w-200 mt-2 px-4 py-2 flex items-center justify-center gap-2"
              disabled={isLoading || isFormDisabled || activePolygonIndex < 0 || activePolygonIndex >= polygons.length}
            >
              <AiOutlineSearch size={18} />
              {isLoading ? 'Loading...' : 'Fetch data'}
            </Button>
          </Card>

          <Card title="Busiest Days">
            {isLoading ? (
              <div className="flex justify-center items-center h-full min-w-[400px]">
                <p>Loading data...</p>
              </div>
            ) : (
              <Table
                cols={[
                  { title: 'Date', field: 'date', order: true },
                  { title: 'Unique Visitors', field: 'unique_visitors', order: true },
                  { title: 'Avg Time Spent', field: 'avg_time_spent_seconds', order: true },
                  { title: 'Total Time Spent', field: 'total_time_spent_seconds', order: true },
                ]}
                rows={busiestDays.length > 0 ? busiestDays : [{ date: '-', unique_visitors: '-', avg_time_spent_seconds: '-', total_time_spent_seconds: '-' }]}
              />
            )}
          </Card>

        </div>
        <Card title="Coordinates" className="p-2">
          <form onSubmit={handleSubmit(onSubmit)}>
            <CoordinateInputs
              register={register}
              errors={errors}
              handleFormChange={handleFormChange}
              disabled={isFormDisabled}
            />
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default Sandbox;
